import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";

export type SelectOption = { value: string; label: string; description?: string };

export default function SearchableSelect({ label, value, options, onChange, disabled = false, placeholder = "Select an option", searchPlaceholder = "Search organizations", emptyMessage = "No matching organizations." }: {
  label: string; value: string; options: SelectOption[]; onChange: (value: string) => void;
  disabled?: boolean; placeholder?: string; searchPlaceholder?: string; emptyMessage?: string;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState<CSSProperties>({});
  const filtered = options.filter(option => `${option.label} ${option.description ?? ""}`.toLowerCase().includes(search.trim().toLowerCase()));
  const selected = options.find(option => option.value === value);
  const activeIndex = Math.min(active, filtered.length - 1);

  function close(restoreFocus = false) {
    setOpen(false);
    setSearch("");
    if (restoreFocus) trigger.current?.focus();
  }
  function choose(option: SelectOption) {
    if (disabled) return;
    onChange(option.value);
    close(true);
  }
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!trigger.current?.contains(event.target as Node) && !popup.current?.contains(event.target as Node)) close();
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  useEffect(() => {
    if (open) popup.current?.querySelector(`[data-option-index="${activeIndex}"]`)?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex, search]);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = trigger.current?.getBoundingClientRect();
      if (!rect) return;
      const viewport = window.visualViewport;
      const topEdge = viewport?.offsetTop ?? 0;
      const leftEdge = viewport?.offsetLeft ?? 0;
      const width = viewport?.width ?? window.innerWidth;
      const bottom = topEdge + (viewport?.height ?? window.innerHeight);
      const below = bottom - rect.bottom - 12;
      const above = rect.top - topEdge - 12;
      const aboveTrigger = below < 240 && above > below;
      const maxHeight = Math.max(0, Math.min(320, aboveTrigger ? above : below));
      const menuWidth = Math.min(Math.max(rect.width, 280), width - 16);
      setPosition({
        width: menuWidth, maxHeight,
        left: Math.max(leftEdge + 8, Math.min(rect.left, leftEdge + width - menuWidth - 8)),
        ...(aboveTrigger ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 })
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
    };
  }, [open]);

  return <div className="pa-searchable-select">
    <button ref={trigger} type="button" className="pa-searchable-trigger" disabled={disabled}
      aria-label={label} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? `${id}-popup` : undefined}
      onClick={() => { if (open) close(); else { setActive(Math.max(0, options.findIndex(o => o.value === value))); setOpen(true); } }}
      onKeyDown={event => { if (event.key === "ArrowDown") { event.preventDefault(); setActive(0); setOpen(true); } }}>
      <span>{selected?.label ?? placeholder}</span><ChevronDown size={16} aria-hidden="true" />
    </button>
    {open && !disabled && createPortal(<div id={`${id}-popup`} ref={popup} className="pa-searchable-popup" style={position} role="dialog" aria-label={label}
      onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node) && event.relatedTarget !== trigger.current) close(); }}
      onKeyDown={event => {
        if (event.key === "Escape") { event.preventDefault(); close(true); }
        else if (event.key === "Tab") close(true);
        else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (filtered.length) setActive((activeIndex + (event.key === "ArrowDown" ? 1 : -1) + filtered.length) % filtered.length);
        } else if (event.key === "Enter") { event.preventDefault(); if (filtered[activeIndex]) choose(filtered[activeIndex]); }
      }}>
      <label className="pa-search-field"><Search size={16} aria-hidden="true" /><input autoFocus role="combobox"
        aria-label={`Search ${label.toLowerCase()}`} aria-expanded="true" aria-controls={`${id}-options`}
        aria-autocomplete="list" aria-activedescendant={activeIndex >= 0 ? `${id}-${activeIndex}` : undefined}
        placeholder={searchPlaceholder} value={search} onChange={event => { setSearch(event.target.value); setActive(0); }} /></label>
      <div id={`${id}-options`} role="listbox" aria-label={label} className="pa-searchable-options">
        {filtered.map((option, index) => <button key={option.value} id={`${id}-${index}`} data-option-index={index} type="button" role="option"
          tabIndex={-1} aria-selected={option.value === value} className={index === activeIndex ? "active" : ""}
          onPointerDown={event => event.preventDefault()} onMouseEnter={() => setActive(index)} onClick={() => choose(option)}>
          <span>{option.label}{option.description && <small>{option.description}</small>}</span>
          {option.value === value && <Check size={16} aria-hidden="true" />}
        </button>)}
        {!filtered.length && <p className="pa-form-note" role="status">{emptyMessage}</p>}
      </div>
    </div>, trigger.current?.closest("dialog") ?? document.body)}
  </div>;
}
